import React, { useState } from 'react';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { nb } from 'date-fns/locale';
import { 
  addMonths, 
  format, 
  isSameDay, 
  isWithinInterval, 
  startOfDay 
} from 'date-fns';
import { mockBookings } from '@/data/mockBookings';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Dialog, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import BookingDetailsDialog from './BookingDetailsDialog';
import WeeklyCalendar from './WeeklyCalendar';
import VisualMonthCalendar from './VisualMonthCalendar';
import { Booking } from '@/types/booking';
import { toast } from 'sonner';

const BookingCalendar: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());
  const [month, setMonth] = useState<Date>(new Date());
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  
  // Handler for deleting bookings
  const handleDeleteBooking = (bookingId: string) => {
    // In a real application, this would call an API to delete the booking
    toast.success(`Booking ${bookingId} deleted successfully`);
  };
  
  // Get bookings for the selected date
  const bookingsForDate = selectedDate 
    ? mockBookings.filter(booking => 
        isWithinInterval(startOfDay(selectedDate), {
          start: startOfDay(booking.startDate),
          end: startOfDay(booking.endDate)
        })
      )
    : [];
  
  // Function to determine which dates have bookings
  const isDayWithBooking = (day: Date) => {
    const hasBooking = mockBookings.some(booking => 
      isWithinInterval(startOfDay(day), {
        start: startOfDay(booking.startDate),
        end: startOfDay(booking.endDate)
      })
    );
    
    return hasBooking;
  };
  
  // Custom day render to highlight days with bookings
  const renderDay = (day: Date) => {
    const isToday = isSameDay(day, new Date());
    const dayBookings = mockBookings.filter(booking => 
      isWithinInterval(startOfDay(day), {
        start: startOfDay(booking.startDate),
        end: startOfDay(booking.endDate)
      })
    );
    
    const hasBooking = dayBookings.length > 0;
    
    return (
      <div className={`relative p-2 text-center w-full ${isToday ? 'font-bold' : ''}`}>
        {day.getDate()}
        {hasBooking && (
          <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 flex gap-0.5">
            {dayBookings.slice(0, 3).map((booking, index) => (
              <div 
                key={booking.id} 
                className="w-1.5 h-1.5 bg-green-500 rounded-full" 
                title={`${booking.companyName} - ${booking.articleTitle || 'Ingen tittel'}`}
              />
            ))}
            {dayBookings.length > 3 && (
              <div className="w-1.5 h-1.5 bg-blue-500 rounded-full" title={`+${dayBookings.length - 3} flere bookinger`} />
            )}
          </div>
        )}
      </div>
    );
  };
  
  return (
    <div className="space-y-6">
      <Tabs defaultValue="ukeoversikt" className="w-full">
        <TabsList>
          <TabsTrigger value="ukeoversikt">Ukeoversikt</TabsTrigger>
          <TabsTrigger value="månedsoversikt">Månedsoversikt</TabsTrigger>
        </TabsList>
        
        <TabsContent value="ukeoversikt" className="mt-6">
          <WeeklyCalendar bookings={mockBookings} />
        </TabsContent>
        
        <TabsContent value="månedsoversikt" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <Card>
                <CardContent className="p-0">
                  <VisualMonthCalendar
                    currentMonth={month}
                    selectedDate={selectedDate}
                    bookings={mockBookings}
                    onDateSelect={setSelectedDate}
                    onMonthChange={setMonth}
                  />
                </CardContent>
              </Card>
            </div>
            
            <div>
              <Card>
                <CardHeader>
                  <CardTitle>
                    {selectedDate ? format(selectedDate, 'dd. MMMM yyyy', { locale: nb }) : 'Velg en dato'}
                  </CardTitle>
                  <CardDescription>
                    {bookingsForDate.length === 0 ? 'Ingen bookinger denne dagen' : `${bookingsForDate.length} bookinger`}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {bookingsForDate.length > 0 ? (
                    <ul className="space-y-3">
                      {bookingsForDate.map(booking => (
                        <Dialog key={booking.id}>
                          <DialogTrigger asChild>
                            <li 
                              className="p-3 border rounded-md cursor-pointer hover:bg-gray-50 transition-colors w-full text-left"
                              onClick={() => setSelectedBooking(booking)}
                            >
                              <div className="font-medium">{booking.companyName}</div>
                              <div className="text-sm text-muted-foreground">
                                {booking.articleTitle || 'Ingen tittel'}
                              </div>
                              <div className="flex items-center justify-between mt-1">
                                <div className="text-xs text-muted-foreground">
                                  {format(booking.startDate, 'dd.MM.yy', { locale: nb })} - {format(booking.endDate, 'dd.MM.yy', { locale: nb })}
                                </div>
                                <div className={`text-xs px-2 py-0.5 rounded-full ${
                                  booking.status === 'aktiv' ? 'bg-green-100 text-green-800' : 
                                  booking.status === 'planlagt' ? 'bg-blue-100 text-blue-800' : 
                                  booking.status === 'fullført' ? 'bg-gray-100 text-gray-800' : 
                                  'bg-red-100 text-red-800'
                                }`}>
                                  {booking.status}
                                </div>
                              </div>
                            </li>
                          </DialogTrigger>
                          
                          <BookingDetailsDialog booking={booking} onDelete={handleDeleteBooking} />
                        </Dialog>
                      ))}
                    </ul>
                  ) : (
                    <div className="text-center py-6 text-gray-500">
                      <p>Ingen bookinger for valgt dato</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default BookingCalendar;
